/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Fragment } from 'react';
import { EuiI18nConsumer } from '../context';
import { processStringToChildren } from './i18n_util';

function errorOnMissingValues(token) {
  throw new Error("I18n mapping for token \"".concat(token, "\" is a formatting function but no values were provided."));
}

function lookupToken(token, i18nMapping, valueDefault, i18nMappingFunc, values) {
  var renderable = i18nMapping && i18nMapping[token] || valueDefault;

  if (typeof renderable === 'function') {
    if (values === undefined) {
      return errorOnMissingValues(token);
    } else {
      // @ts-ignore-next-line
      // TypeScript complains that `DEFAULT` doesn't have a call signature
      // but we verified `renderable` is a function
      return renderable(values);
    }
  } else if (values === undefined || typeof renderable !== 'string') {
    if (i18nMappingFunc && typeof valueDefault === 'string') {
      renderable = i18nMappingFunc(valueDefault);
    } // there's a hole in the typings here as there is no guarantee that i18nMappingFunc
    // returned the same type of the default value, but we need to keep that assumption


    return renderable;
  }

  var children = processStringToChildren(renderable, values, i18nMappingFunc);

  if (typeof children === 'string') {
    // likewise, `processStringToChildren` returns a string or ReactChild[] depending on
    // the type of `values`, so we will make the assumption that the default value is correct.
    return children;
  }

  var Component = () => {
    return <Fragment>{children}</Fragment>;
  }; // same reasons as above, we can't promise the transforms match the default's type


  return React.createElement(Component, values);
}

function hasTokens(x) {
  return x.tokens != null;
} // Must use the generics <T extends {}>
// If instead typed with React.FunctionComponent there isn't feedback given back to the dev
// when using a `values` object with a renderer callback.


var EuiI18n = props => <EuiI18nConsumer>
    {i18nConfig => {
    const {
      mapping,
      mappingFunc
    } = i18nConfig;

    if (hasTokens(props)) {
      return props.children(props.tokens.map((token, idx) => lookupToken(token, mapping, props.defaults[idx], mappingFunc)));
    }

    const tokenValue = lookupToken(props.token, mapping, props.default, mappingFunc, props.values);

    if (props.children) {
      return props.children(tokenValue);
    } else {
      return tokenValue;
    }
  }}
  </EuiI18nConsumer>;

export { EuiI18n };

try {
  // @ts-ignore
  i18n.displayName = "i18n"; // @ts-ignore

  i18n.__docgenInfo = {
    "description": "",
    "displayName": "i18n",
    "props": {
      "default": {
        "defaultValue": null,
        "description": "",
        "name": "default",
        "required": false,
        "type": {
          "name": "DEFAULT | undefined"
        }
      },
      "values": {
        "defaultValue": null,
        "description": "",
        "name": "values",
        "required": false,
        "type": {
          "name": "T | undefined"
        }
      },
      "token": {
        "defaultValue": null,
        "description": "",
        "name": "token",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "tokens": {
        "defaultValue": null,
        "description": "",
        "name": "tokens",
        "required": false,
        "type": {
          "name": "string[] | undefined"
        }
      },
      "defaults": {
        "defaultValue": null,
        "description": "",
        "name": "defaults",
        "required": false,
        "type": {
          "name": "DEFAULTS | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/i18n/i18n.tsx#i18n"] = {
      docgenInfo: i18n.__docgenInfo,
      name: "i18n",
      path: "src/components/i18n/i18n.tsx#i18n"
    };
} catch (__react_docgen_typescript_loader_error) {}